import React from "react";
import Card from "./Card";
import { LucideIcon } from "lucide-react";

/**
 * StatCard Component
 * A premium looking card for dashboard metrics
 * 
 * @param {Object} props
 * @param {string} props.title - The title of the metric
 * @param {string|number} props.value - The main value to display
 * @param {string} [props.subtitle] - Optional subtitle or trend info
 * @param {LucideIcon} props.icon - Lucide icon component
 * @param {string} [props.variant] - color variant: 'primary', 'secondary', 'success', 'warning', 'error', 'info'
 * @param {string} [props.className] - Additional classes
 * @param {Function} [props.onClick] - Optional click handler
 */
export const StatCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    variant = "primary",
    className = "",
    onClick,
}) => {
    const variants = {
        primary: "from-blue-500/10 to-blue-600/10 text-blue-600 bg-blue-50/50 border-blue-100",
        secondary: "from-orange-500/10 to-orange-600/10 text-orange-600 bg-orange-50/50 border-orange-100",
        success: "from-emerald-500/10 to-emerald-600/10 text-emerald-600 bg-emerald-50/50 border-emerald-100",
        warning: "from-amber-500/10 to-amber-600/10 text-amber-600 bg-amber-50/50 border-amber-100",
        error: "from-rose-500/10 to-rose-600/10 text-rose-600 bg-rose-50/50 border-rose-100",
        info: "from-indigo-500/10 to-indigo-600/10 text-indigo-600 bg-indigo-50/50 border-indigo-100",
    };

    const iconVariants = {
        primary: "bg-blue-500 text-white shadow-blue-200",
        secondary: "bg-orange-500 text-white shadow-orange-200",
        success: "bg-emerald-500 text-white shadow-emerald-200",
        warning: "bg-amber-500 text-white shadow-amber-200",
        error: "bg-rose-500 text-white shadow-rose-200",
        info: "bg-indigo-500 text-white shadow-indigo-200",
    };

    const selectedVariant = variants[variant] || variants.primary;
    const selectedIconVariant = iconVariants[variant] || iconVariants.primary;

    // Helper to format numbers with Brazilian locale (dots as thousand separators)
    const formatValue = (val) => {
        if (typeof val === 'number') {
            return new Intl.NumberFormat('pt-BR').format(val);
        }
        if (typeof val === 'string' && !isNaN(val)) {
            return new Intl.NumberFormat('pt-BR').format(Number(val));
        }
        return val;
    };

    return (
        <Card
            className={`relative overflow-hidden group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${onClick ? "cursor-pointer active:scale-95" : ""
                } ${className}`}
            onClick={onClick}
            styles={{ body: { padding: 0 } }}
        >
            <div className={`absolute inset-0 bg-gradient-to-br ${selectedVariant} opacity-40 group-hover:opacity-60 transition-opacity`} />

            <div className="relative p-5 sm:p-6 flex flex-col justify-between min-h-[140px]">
                <div className="flex justify-between items-start mb-4">
                    <p className="text-[10px] sm:text-xs font-bold text-muted-foreground uppercase tracking-widest opacity-70">
                        {title}
                    </p>
                    <div className={`p-2 rounded-xl shadow-md transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3 ${selectedIconVariant}`}>
                        <Icon className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.5} />
                    </div>
                </div>

                <div className="mt-auto">
                    <div className="flex items-baseline gap-2">
                        {typeof value === 'string' && value.includes('/') ? (
                            <div className="flex flex-col">
                                <span className="text-3xl sm:text-4xl font-black tracking-tighter text-foreground leading-none">
                                    {formatValue(value.split('/')[0])}
                                </span>
                                <span className="text-xs sm:text-sm font-bold text-muted-foreground/60 mt-1 uppercase tracking-tighter">
                                    de {formatValue(value.split('/')[1])} total
                                </span>
                            </div>
                        ) : (
                            <h3 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight text-foreground truncate w-full" title={String(value)}>
                                {formatValue(value)}
                            </h3>
                        )}
                    </div>
                    {subtitle && (
                        <p className="text-[10px] sm:text-xs font-semibold text-muted-foreground/80 mt-1 truncate">
                            {subtitle}
                        </p>
                    )}
                </div>
            </div>

            {/* Subtle background decoration */}
            <div className="absolute -right-6 -bottom-6 opacity-[0.05] group-hover:opacity-[0.08] transition-opacity pointer-events-none transform rotate-12">
                <Icon size={140} />
            </div>
        </Card>
    );
};
